import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { useEffectiveRecordPropertySchemas } from "~/features/recordDetails/hooks/useEffectiveRecordPropertySchemas";
import { useRelationActivation, useRelationDetail } from "~/features/relations/hooks";
import type { RelationDetailTab } from "~/features/relations/types";
import {
  toRelationChangeRequestRows,
  toRelationDetailUI,
  toRelationEvidenceRows,
  toRelationLineage,
} from "~/features/relations/utils/transformers";
import { v2Route } from "~/v2/routes";
import { RelationActivationDialog } from "./RelationActivationDialog";
import { RelationDetailPresenter } from "./RelationDetailPresenter";

interface RelationDetailProps {
  id: string;
  onBack: () => void;
  variant?: "page" | "peek";
}

export function RelationDetail({ id, onBack, variant = "page" }: RelationDetailProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const navigate = useNavigate();
  const [params, setParams] = useTypedSearchParams(v2Route.relations);
  const { data, refetch: refetchDetail, recordGeoItems } = useRelationDetail(id);
  const { onActivate, onDeactivate, processing } = useRelationActivation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const geoItemId = params.geoItemId ?? undefined;
  const activeTab: RelationDetailTab = params["tab-relationDetail"] ?? "data";
  const onTabChange = useCallback(
    (next: RelationDetailTab) => {
      setParams((prev) => ({ ...prev, "tab-relationDetail": next === "data" ? undefined : next }));
    },
    [setParams],
  );

  const raw = data.records[0];
  const { schemas: effectiveSchemas, refetch: refetchEffectiveSchemas } = useEffectiveRecordPropertySchemas(
    raw?.recordMetadata.recordMetadataCategories.map(({ category }) => category.id) ?? [],
  );
  const refetch = useCallback(async () => {
    const updated = await refetchDetail();
    const categoryIDs =
      updated.data?.records[0]?.recordMetadata.recordMetadataCategories.map(({ category }) => category.id) ?? [];
    await refetchEffectiveSchemas(categoryIDs);
    return updated;
  }, [refetchDetail, refetchEffectiveSchemas]);
  const detail = useMemo(
    () => (raw ? toRelationDetailUI(raw, locale, recordGeoItems, effectiveSchemas) : null),
    [raw, locale, recordGeoItems, effectiveSchemas],
  );
  const changeRequestRows = useMemo(() => (raw ? toRelationChangeRequestRows(raw) : []), [raw]);
  const evidenceRows = useMemo(() => (raw ? toRelationEvidenceRows(raw) : []), [raw]);
  const lineage = useMemo(
    () =>
      raw
        ? toRelationLineage(raw, {
            record: detail?.displayName || raw.id,
            changeRequest: t("model.changeRequest.modelName"),
          })
        : { nodes: [], edges: [] },
    [raw, detail, t],
  );

  const handleCopyId = useCallback(
    async (value: string) => {
      try {
        await copyToClipboard(value);
        toast.success(t("feature.common.idCopied"));
      } catch {
        toast.error(t("feature.common.copyToClipboardFailed"));
      }
    },
    [t],
  );

  const handleConfirm = useCallback(async () => {
    if (!raw) return;
    const target = { id: raw.id, recordMetadataId: raw.recordMetadata.id };
    const ok = await (raw.deactivatedAt ? onActivate(target) : onDeactivate(target));
    setConfirmOpen(false);
    if (ok) void refetch();
  }, [raw, onActivate, onDeactivate, refetch]);

  if (!raw || !detail) {
    return <div className="mx-auto max-w-[var(--content-max)] p-8 text-[13px] text-fg-3">{t("ui.notFound")}</div>;
  }

  return (
    <>
      <RelationDetailPresenter
        detail={detail}
        onBack={onBack}
        onCopyId={handleCopyId}
        onRequestToggleActivation={() => setConfirmOpen(true)}
        onSelectEntity={(entityId) => navigate(`/entities/${entityId}`)}
        geoItemId={geoItemId}
        activeTab={activeTab}
        onTabChange={onTabChange}
        changeRequests={changeRequestRows}
        evidences={evidenceRows}
        lineage={lineage}
        variant={variant}
      />
      <RelationActivationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        deactivate={!detail.isDeactivated}
        processing={processing}
        onConfirm={handleConfirm}
      />
    </>
  );
}
